/**
 * Pantalla del **Cotizador de puertas** (`/herramientas/cotizador`). Reemplaza el
 * `PlaceholderPage` de `ROUTES.cotizador`. Equivale a `cotizador_puertas.html` del SSR
 * (demolido en esta feature). NO toca el motor de cálculo (`cotizador_*.py`): consume la API
 * REST que ya lo envuelve.
 *
 * Orquesta el flujo del SSR sobre la API REST:
 *  - **Cotizar**: `POST /api/v1/cotizador/calcular` (mutación). El motor calcula en el
 *    servidor; aquí se muestra total + ticket + imágenes.
 *  - **Ticket por rol**: admin ve el ticket técnico que devuelve `/calcular`; el no-admin ve
 *    el ticket de cliente + producción (se piden con `/ticket_cliente` y `/ticket_produccion`
 *    y se concatenan, igual que el SSR).
 *  - **Historial**: tras un cálculo exitoso se guarda en `/cotizador/cotizaciones` (como el
 *    SSR al pulsar COTIZAR). Las pestañas de Estadísticas e Historial son solo admin.
 *
 * El resultado del cálculo es estado de UI local (`useState`): es una mutación dependiente
 * del formulario, no dato de servidor idempotente (`docs/frontend_conventions.md`, Datos).
 * Solo el historial (listar/stats/guardar) es dato de servidor con TanStack Query. Exige
 * `use_cotizador` vía `RequireCotizadorPermission` (paridad con el menú/SSR/API).
 */
import { useCallback, useState } from "react";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from "@/components/ui";
import { useAuth } from "@/lib/auth";

import type { SaveCotizacionBody } from "../api";
import { cotizadorErrorMessage } from "../format";
import {
  useCalcular,
  useGuardarCotizacion,
  useTicketCliente,
  useTicketProduccion,
} from "../hooks/useCotizador";
import type { CalcResult, CotizadorInput } from "../types";
import { CotizadorForm } from "./CotizadorForm";
import { HistoryPanel } from "./HistoryPanel";
import { RequireCotizadorPermission } from "./RequireCotizadorPermission";
import { ResultPanel } from "./ResultPanel";
import { StatsPanel } from "./StatsPanel";

/** Pestañas de la pantalla (Estadísticas e Historial solo se montan para admin). */
type Tab = "cotizacion" | "estadisticas" | "historial";

function CotizadorWorkspace() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [tab, setTab] = useState<Tab>("cotizacion");
  const [result, setResult] = useState<CalcResult | null>(null);
  const [ticketText, setTicketText] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const calcular = useCalcular();
  const guardar = useGuardarCotizacion();
  const ticketCliente = useTicketCliente();
  const ticketProduccion = useTicketProduccion();

  // `loading` cubre el cálculo y, para no-admin, la preparación de los tickets.
  const loading =
    calcular.isPending || ticketCliente.isPending || ticketProduccion.isPending;

  const persistHistory = useCallback(
    (input: CotizadorInput, calc: CalcResult) => {
      // Réplica de `persistCotizacionHistorial`: guarda en silencio (un fallo de historial no
      // debe romper la cotización; el error se ignora como en el SSR).
      const body: SaveCotizacionBody = {
        ...input,
        total_cotizador: calc.total,
        m2_total: calc.m2_total,
        peso_estimado_kg: calc.peso_estimado_kg,
      };
      guardar.mutate(body);
    },
    [guardar],
  );

  const handleSubmit = useCallback(
    async (input: CotizadorInput) => {
      setErrorMsg(null);
      try {
        const calc = await calcular.mutateAsync(input);
        setResult(calc);

        if (isAdmin) {
          // Admin: el ticket técnico ya viene en el cálculo.
          setTicketText(calc.ticket);
        } else {
          // No-admin: pide cliente + producción y los concatena (igual que el SSR).
          const [cliente, produccion] = await Promise.all([
            ticketCliente.mutateAsync(input),
            ticketProduccion.mutateAsync(input),
          ]);
          setTicketText(`${cliente.ticket ?? ""}${produccion.ticket ?? ""}`);
        }

        persistHistory(input, calc);
      } catch (err) {
        // Error del cálculo o de los tickets: mensaje legible, sin pantalla en blanco.
        setErrorMsg(cotizadorErrorMessage(err));
        setResult(null);
        setTicketText(null);
      }
    },
    [calcular, isAdmin, persistHistory, ticketCliente, ticketProduccion],
  );

  const handleReset = useCallback(() => {
    setResult(null);
    setTicketText(null);
    setErrorMsg(null);
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Cotizador de puertas</h1>
        <p className="text-sm text-slate-500">
          Cotización, auditoría y ticket detallado.
        </p>
      </div>

      {isAdmin ? (
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Secciones del cotizador">
          <TabButton active={tab === "cotizacion"} onClick={() => setTab("cotizacion")}>
            Cotización
          </TabButton>
          <TabButton active={tab === "estadisticas"} onClick={() => setTab("estadisticas")}>
            Estadísticas
          </TabButton>
          <TabButton active={tab === "historial"} onClick={() => setTab("historial")}>
            Historial
          </TabButton>
        </div>
      ) : null}

      {tab === "cotizacion" ? (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,420px)_1fr]">
          <Card>
            <CardHeader>
              <CardTitle>Panel de ingreso</CardTitle>
            </CardHeader>
            <CardContent>
              {errorMsg ? (
                <p role="alert" className="mb-3 text-sm text-red-600">
                  {errorMsg}
                </p>
              ) : null}
              <CotizadorForm
                isAdmin={!!isAdmin}
                calculating={loading}
                onSubmit={handleSubmit}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Resultado</CardTitle>
            </CardHeader>
            <CardContent>
              <ResultPanel
                result={result}
                ticketText={ticketText}
                loading={loading}
                onReset={handleReset}
              />
            </CardContent>
          </Card>
        </div>
      ) : null}

      {isAdmin && tab === "estadisticas" ? <StatsPanel lastCalc={result} /> : null}
      {isAdmin && tab === "historial" ? <HistoryPanel /> : null}
    </div>
  );
}

/** Botón de pestaña (presentacional; estado activo resaltado). */
function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      role="tab"
      aria-selected={active}
      variant={active ? "primary" : "secondary"}
      size="sm"
      onClick={onClick}
      className={cn(active ? undefined : "text-slate-600")}
    >
      {children}
    </Button>
  );
}

/** Pantalla del cotizador con el gating de permiso `use_cotizador`. */
export function CotizadorScreen() {
  return (
    <RequireCotizadorPermission>
      <CotizadorWorkspace />
    </RequireCotizadorPermission>
  );
}
